import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'radix-ui';

import { useDepartmentGroups, DEPARTMENT_PATH_RE } from '@/entities/department';
import { useIntermediateTask } from '@/features/tasks/model/useIntermediateTask';
import { useUsers } from '@/hooks/users/useUserActions';
import { cn } from '@/shared/lib/cn';
import { useAuthStore } from '@/store/auth';
import { NotificationBell } from '@/widgets/notifications';

import { DesktopSidebarActiveTimer } from './DesktopSidebarActiveTimer';
import { DesktopSidebarFooter } from './DesktopSidebarFooter';
import { DesktopSidebarHeader } from './DesktopSidebarHeader';
import { DesktopSidebarMenu } from './DesktopSidebarMenu';
import {
  SidebarCollapsedContext,
  useSidebarCollapseStore,
} from './sidebarCollapse';

export function DesktopSidebar() {
  const { userId } = useAuthStore();
  const { data: user } = useUsers.useById(userId);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { groups = [], isLoading: groupsLoading } = useDepartmentGroups();
  const { createIntermediateTask, isLoading: isCreatingTask } =
    useIntermediateTask({ type: 'intermediate' });

  const [otdelsOpen, setOtdelsOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  // Свёрнутый вид — только иконки с подписями по наведению. Переключатель
  // внизу (DesktopSidebarFooter), выбор помнится между сессиями.
  const collapsed = useSidebarCollapseStore((state) => state.collapsed);

  const selectedGroupId = pathname.match(DEPARTMENT_PATH_RE)?.[1] ?? null;
  const selectedGroupName =
    groups.find((group) => String(group.ID) === selectedGroupId)?.Name ??
    'Отделы';

  return (
    <SidebarCollapsedContext.Provider value={collapsed}>
      <Tooltip.Provider delayDuration={150} skipDelayDuration={300}>
        <aside
          className={cn(
            'sticky top-0 z-30 mr-4 hidden h-screen shrink-0 overflow-hidden rounded-r-[20px] border-r border-[#EEF0F4] bg-white font-roboto text-[#4B5563] shadow-[0_10px_34px_rgba(17,24,39,0.08)] lg:flex 2xl:mr-6 pb-[clamp(4px,0.5vh,8px)]',
            'transition-[width] duration-200 ease-out',
            collapsed ? 'w-[76px]' : 'w-[220px]'
          )}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div
              className={
                collapsed
                  ? 'flex flex-col items-center gap-1.5 px-3 pt-3 pb-2'
                  : 'flex min-h-[73px] items-center gap-2 px-3'
              }
            >
              <div className={collapsed ? undefined : 'min-w-0 flex-1'}>
                <DesktopSidebarHeader
                  avatarOpen={avatarOpen}
                  user={user ?? undefined}
                  onAvatarOpenChange={setAvatarOpen}
                  onNavigate={navigate}
                />
              </div>
              <NotificationBell />
            </div>

            <div className="px-3 pb-[8px]">
              <DesktopSidebarActiveTimer />
            </div>

            <DesktopSidebarMenu
              groups={groups}
              groupsLoading={groupsLoading}
              otdelsOpen={otdelsOpen}
              selectedGroupId={selectedGroupId}
              selectedGroupName={selectedGroupName}
              onNavigate={navigate}
              onOtdelsOpenChange={setOtdelsOpen}
            />

            <div className="px-3">
              <DesktopSidebarFooter
                userId={userId}
                isCreatingTask={isCreatingTask}
                onCreateIntermediateTask={createIntermediateTask}
                onControlClick={() => navigate('/?control=1')}
              />
            </div>
          </div>
        </aside>
      </Tooltip.Provider>
    </SidebarCollapsedContext.Provider>
  );
}
